import type { AidenRequest, AidenResponse, Config, IWebshopAPI } from "../types"
import type { InternalAnalyticsEvent } from "../../analytics/internal/types"
import { envVar, EnvVar } from "../setup/env"
import { persistVisitorId } from "../../analytics/integrations/visitor"

const advisorAPI = (envVar(EnvVar.ADVISOR_API) || "") as string
const advisorPreviewAPI = (envVar(EnvVar.ADVISOR_PREVIEW_API) || "") as string
const DOMAIN = `${advisorAPI}/v1/forms`
const PREVIEW_DOMAIN = `${advisorPreviewAPI}/v1/forms`

const headers = {
  Accept: "application/json, text/plain, */*",
  "Content-Type": "application/json",
}

const initData = {
  context: {},
  entities: [],
  history: [],
  screenId: null,
  sessionId: null,
  testVariant: null,
  clientInfo: null,
}

function withHostPage(body: Record<string, unknown>) {
  return { ...body, hostPage: document.referrer || null }
}

const getDomain = (preview: boolean) => {
  return preview ? PREVIEW_DOMAIN : DOMAIN
}

const WebshopAPI: (visitorId: string) => IWebshopAPI = (visitorId) => {
  function saveVisitorId() {
    // Save the in-memory visitorId after every API call to make sure its persisted
    // and consistent if the storage is cleared during the usage of an advisor.
    persistVisitorId(visitorId)
  }

  // Abort requests
  let controller: undefined | AbortController = undefined

  const abortable = <T>(
    fn: (signal: AbortSignal | null) => Promise<T>
  ): Promise<T> => {
    if (typeof AbortController !== "undefined") {
      controller?.abort()
      controller = new AbortController()
      return fn(controller.signal)
    }
    return fn(null)
  }

  const handleError = (e: unknown) => {
    if (e instanceof Error) {
      // Aborting the request raises an error which should be muted
      if (e.name !== "AbortError") {
        throw e
      } else {
        throw Error("Aborted request")
      }
    }
  }

  const init = (
    advisorId: string,
    preview = false,
    config: Config,
    testVariant: string | null,
    sessionId: string | null
  ): Promise<AidenResponse> =>
    // This code is confirmed to work and a risk has been weighed.
    // We expect the REST API to be correct and return the correct structure.
    // Hence we don't 'decode' the response but assume its correct, saving on bundle size.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    abortable((signal: AbortSignal | null) =>
      fetch(
        `${getDomain(preview)}/init/${advisorId}?preview=${
          preview ? "true" : "false"
        }&mode=${config.mode}`,
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify(
            enhanceBody(
              visitorId,
              config
            )({
              ...initData,
              testVariant,
              sessionId,
              clientInfo: {
                userAgent: navigator.userAgent,
              },
            })
          ),
          signal,
        }
      )
        .then((resp) => {
          if (resp.ok) return resp.json()
          throw {
            status: resp.status,
            msg: "Not found",
          }
        })
        .catch(handleError)
        .finally(saveVisitorId)
    )

  const submit = (
    advisorId: string,
    preview = false,
    config: Config,
    data: AidenRequest
  ): Promise<AidenResponse> =>
    // This code is confirmed to work and a risk has been weighed.
    // We expect the REST API to be correct and return the correct structure.
    // Hence we don't 'decode' the response but assume its correct, saving on bundle size.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    abortable((signal: AbortSignal | null) =>
      fetch(
        `${getDomain(preview)}/submit/${advisorId}?preview=${boolToString(
          preview
        )}&mode=${config.mode}`,
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify(enhanceBody(visitorId, config)(data)),
          signal,
        }
      )
        .then((d) => d.json())
        .catch(handleError)
        .finally(saveVisitorId)
    ).then((d) => addProductScopeToResponse(config)(d))

  const refreshAdviceScreen = (
    advisorId: string,
    preview = false,
    config: Config,
    data: AidenRequest
  ): Promise<AidenResponse> =>
    // This code is confirmed to work and a risk has been weighed.
    // We expect the REST API to be correct and return the correct structure.
    // Hence we don't 'decode' the response but assume its correct, saving on bundle size.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    abortable((signal: AbortSignal | null) =>
      fetch(
        `${getDomain(
          preview
        )}/refresh-advice/${advisorId}?preview=${boolToString(preview)}&mode=${
          config.mode
        }`,
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify(enhanceBody(visitorId, config)(data)),
          signal,
        }
      )
        // .then((d) => d.json().then(withProductScope(config)))
        .then((d) => d.json())
        .catch(handleError)
        .finally(saveVisitorId)
    ).then((d) => addProductScopeToResponse(config)(d))

  const update = (
    advisorId: string,
    preview = false,
    config: Config,
    data: AidenRequest
  ): Promise<AidenResponse> =>
    // This code is confirmed to work and a risk has been weighed.
    // We expect the REST API to be correct and return the correct structure.
    // Hence we don't 'decode' the response but assume its correct, saving on bundle size.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    abortable((signal: AbortSignal | null) =>
      fetch(
        `${getDomain(preview)}/update/${advisorId}?preview=${boolToString(
          preview
        )}&mode=${config.mode}`,
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify(enhanceBody(visitorId, config)(data)),
          signal,
        }
      )
        .then((d) => d.json())
        .catch(handleError)
        .finally(saveVisitorId)
    ).then((d) => addProductScopeToResponse(config)(d))

  const optionsFor = (
    advisorId: string,
    screenId: string,
    preview: boolean,
    optionsForField: string,
    config: Config,
    entities: AidenRequest["entities"] | undefined
  ): Promise<{ value: string }[]> => {
    const body = JSON.stringify(
      enhanceBody(
        visitorId,
        config
      )(
        screenId
          ? {
              optionsFor: optionsForField,
              screenId: screenId,
              entities: entities,
            }
          : {
              optionsFor: optionsForField,
              entities: entities,
            }
      )
    )
    // This code is confirmed to work and a risk has been weighed.
    // We expect the REST API to be correct and return the correct structure.
    // Hence we don't 'decode' the response but assume its correct, saving on bundle size.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return fetch(
      `${getDomain(preview)}/options/${advisorId}?preview=${boolToString(
        preview
      )}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: body,
      }
    ).then((d) => d.json())
  }

  const analytic = (
    advisorId: string,
    preview = false,
    config: Config,
    event: InternalAnalyticsEvent
  ): Promise<AidenResponse> =>
    // This code is confirmed to work and a risk has been weighed.
    // We expect the REST API to be correct and return the correct structure.
    // Hence we don't 'decode' the response but assume its correct, saving on bundle size.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    fetch(
      `${getDomain(preview)}/analytics/${advisorId}?preview=${boolToString(
        preview
      )}&mode=${config.mode}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(enhanceBody(visitorId, config)(event)),
      }
    )
      .then((d) => d.json())
      .catch(handleError)
      .finally(saveVisitorId)

  return {
    init,
    submit,
    refreshAdviceScreen,
    update,
    analytic,
    optionsFor,
  }
}

function enhanceBody(
  visitorId: string,
  config: Config
): (body: Record<string, unknown>) => Record<string, unknown> {
  return (body) =>
    [
      withHostPage,
      withProductScope(config),
      withVisitor(visitorId),
      withPlacementId(config),
    ].reduce((acc, fn) => fn(acc), body)
}

function addProductScopeToResponse(
  config: Config
): (resp: AidenResponse) => AidenResponse {
  const scope: string | null =
    config.productScope === undefined
      ? null
      : "id" in config.productScope
      ? config.productScope.id
      : "url" in config.productScope
      ? config.productScope.url
      : null
  return (resp) => {
    return {
      ...resp,
      productFocus: scope,
    }
  }
}

const boolToString = (bool: boolean): string => (bool ? "true" : "false")
export default WebshopAPI

function withVisitor(
  visitorId: string
): (body: Record<string, unknown>) => Record<string, unknown> {
  return (body: Record<string, unknown>) => ({ ...body, visitorId: visitorId })
}
function withProductScope(
  config: Config
): (body: Record<string, unknown>) => Record<string, unknown> {
  const scope = getProductScope(config)
  return (body) => ({ ...body, productScope: scope })
}

function withPlacementId(
  config: Config
): (body: Record<string, unknown>) => Record<string, unknown> {
  const placementId = getProductScope(config) ? "product-check" : null
  return (body) => ({ ...body, placementId })
}

function getProductScope(
  config: Config
): { url: string } | { id: string } | null {
  return config.productScope === undefined
    ? null
    : config.productScope.type === "product-id"
    ? { id: config.productScope.id }
    : config.productScope.type === "product-url"
    ? { url: config.productScope.url }
    : null
}
